import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import api from '../config/api';
import { useAuth } from './AuthContext';

const CafeContext = createContext();

const TOKEN_STORAGE_KEY = 'currentToken';

const initialState = {
  items: [],
  activeOrders: [],
  expenses: [],
  loading: false,
  error: null,
  // The open till session, or null when the counter is closed
  counterSession: null,
  // False until the session has been fetched once. Without it a closed counter
  // and a counter we have not asked about yet look identical (both null), and
  // the token below would be called stale on every page load.
  counterLoaded: false,
  // Token being built up right now. Persisted so a page refresh mid-order does
  // not start a second token for the same customer.
  currentToken: Number(localStorage.getItem(TOKEN_STORAGE_KEY)) || null
};

function cafeReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_ITEMS':
      return { ...state, items: action.payload, loading: false, error: null };
    case 'SET_ACTIVE_ORDERS':
      return { ...state, activeOrders: action.payload, loading: false };
    case 'SET_EXPENSES':
      return { ...state, expenses: action.payload, loading: false };
    case 'SET_COUNTER_SESSION':
      return { ...state, counterSession: action.payload, counterLoaded: true };
    case 'SET_CURRENT_TOKEN':
      if (action.payload) {
        localStorage.setItem(TOKEN_STORAGE_KEY, String(action.payload));
      } else {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
      }
      return { ...state, currentToken: action.payload };
    case 'ADD_ORDER': {
      // The server merges repeat taps into the existing line, so this is an
      // update as often as it is an insert
      const incoming = action.payload.order;
      const alreadyListed = state.activeOrders.some(order => order._id === incoming._id);

      return {
        ...state,
        activeOrders: alreadyListed
          ? state.activeOrders.map(order => (order._id === incoming._id ? incoming : order))
          : [...state.activeOrders, incoming],
        items: state.items.map(item =>
          item._id === action.payload.item._id
            ? action.payload.item // Use the updated item from the backend
            : item
        )
      };
    }
    case 'DECREASE_ORDER': {
      const { removed, orderId, order, item } = action.payload;

      return {
        ...state,
        activeOrders: removed
          ? state.activeOrders.filter(current => current._id !== orderId)
          : state.activeOrders.map(current => (current._id === orderId ? order : current)),
        items: state.items.map(current => (current._id === item?._id ? item : current))
      };
    }
    case 'COMPLETE_ORDER': {
      // Stock is not touched here - it came out of inventory when the order was
      // placed, and completing the order doesn't change it
      const itemId = action.payload.item?._id || action.payload.item;

      return {
        ...state,
        activeOrders: state.activeOrders.filter(order => order._id !== action.payload._id),
        items: state.items.map(item =>
          item._id === itemId
            ? {
                ...item,
                activeOrders: Math.max(0, item.activeOrders - (action.payload.quantity || 1))
              }
            : item
        )
      };
    }
    case 'REMOVE_ORDER':
      // Cancelling an order puts its stock back, matching the server
      return {
        ...state,
        activeOrders: state.activeOrders.filter(order => order._id !== action.payload._id),
        items: state.items.map(item =>
          item._id === action.payload.item._id
            ? {
                ...item,
                activeOrders: Math.max(0, item.activeOrders - (action.payload.quantity || 1)),
                stock: item.isInventoryItem
                  ? item.stock + (action.payload.quantity || 1)
                  : item.stock
              }
            : item
        )
      };
    case 'ADD_EXPENSE':
      return {
        ...state,
        expenses: [action.payload, ...state.expenses]
      };
    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map(item =>
          item._id === action.payload._id ? action.payload : item
        )
      };
    case 'ADD_ITEM':
      return {
        ...state,
        items: [...state.items, action.payload]
      };
    case 'DELETE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item._id !== action.payload)
      };
    default:
      return state;
  }
}

export function CafeProvider({ children }) {
  const [state, dispatch] = useReducer(cafeReducer, initialState);
  const { isAuthenticated, user } = useAuth();
  // Depend on the role rather than the user object so these callbacks keep a
  // stable identity across re-renders
  const userRole = user?.role;

  /**
   * How many tokens the open session has handed out, or null when the counter
   * is closed (or has not been fetched yet).
   */
  const tokensIssued =
    state.counterSession && state.counterSession.status !== 'closed'
      ? state.counterSession.tokenSequence ?? 0
      : null;

  /**
   * A stored token from an earlier session is meaningless: token numbers restart
   * at 1 when the counter reopens, so carrying one over would attach new lines
   * to whatever customer now holds that number. The counter may also have been
   * closed from another till or the mobile app, which this browser only learns
   * about on the next poll - so the number is re-checked against the live
   * session rather than trusted because it is in localStorage.
   *
   * The raw value is left in storage: while the session is still loading we
   * simply do not treat it as current, and a page refresh mid-order gets it back.
   */
  const tokenIsCurrent =
    state.currentToken !== null && tokensIssued !== null && state.currentToken <= tokensIssued;

  const currentToken = tokenIsCurrent ? state.currentToken : null;

  // The stored token belongs to a session that has since been closed
  const tokenIsStale = state.counterLoaded && state.currentToken !== null && !tokenIsCurrent;

  // API functions
  const fetchItems = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // A super admin has to choose a tenant before there is anything to show
      if (userRole === 'super_admin' && !localStorage.getItem('tenantId')) {
        dispatch({ type: 'SET_ITEMS', payload: [] });
        return;
      }

      const response = await api.get('/api/items');
      dispatch({ type: 'SET_ITEMS', payload: response.data });
    } catch (error) {
      console.error('Error fetching items:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [userRole]);

  const fetchActiveOrders = useCallback(async () => {
    try {
      // Same rule as items - no tenant selected means nothing to list
      if (userRole === 'super_admin' && !localStorage.getItem('tenantId')) {
        dispatch({ type: 'SET_ACTIVE_ORDERS', payload: [] });
        return;
      }

      const response = await api.get('/api/orders/active');
      dispatch({ type: 'SET_ACTIVE_ORDERS', payload: response.data });
    } catch (error) {
      console.error('Error fetching active orders:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, [userRole]);

  const fetchCounterSession = useCallback(async () => {
    try {
      const response = await api.get('/api/counter/current');
      dispatch({ type: 'SET_COUNTER_SESSION', payload: response.data.session });
      return response.data.session;
    } catch (error) {
      console.error('Error fetching counter session:', error);
      return null;
    }
  }, []);

  const openCounter = async (openingCash) => {
    const response = await api.post('/api/counter/open', { openingCash });
    dispatch({ type: 'SET_COUNTER_SESSION', payload: response.data.session });
    dispatch({ type: 'SET_CURRENT_TOKEN', payload: null });
    return response.data;
  };

  const closeCounter = async (closingCash, notes) => {
    const response = await api.post('/api/counter/close', { closingCash, notes });
    dispatch({ type: 'SET_COUNTER_SESSION', payload: null });
    dispatch({ type: 'SET_CURRENT_TOKEN', payload: null });
    return response.data;
  };

  // Drawer figures for the open session, without closing anything
  const fetchCounterSummary = useCallback(async () => {
    const response = await api.get('/api/counter/summary');
    return response.data;
  }, []);

  // Closed sessions, newest first
  const fetchCounterHistory = useCallback(async (limit = 30) => {
    const response = await api.get('/api/counter/history', { params: { limit } });
    return response.data;
  }, []);

  // Take the next token number. Nothing can be ordered until this has been done,
  // so the customer has a token in hand before any item is rung up.
  const issueToken = async () => {
    const response = await api.post('/api/counter/token');
    // The session's issued count moved, and the token below is only treated as
    // current while it is within that count - so refresh the session too.
    await fetchCounterSession();
    dispatch({ type: 'SET_CURRENT_TOKEN', payload: response.data.tokenNumber });
    return response.data.tokenNumber;
  };

  /**
   * The token to put the next item on: the one being served, or a fresh one.
   *
   * Tapping an item is enough to start a bill, which is how the mobile app
   * behaves - the cashier does not have to remember a separate "create token"
   * step before the first item of every order.
   */
  const ensureToken = async () => {
    if (currentToken) {
      return currentToken;
    }
    return issueToken();
  };

  // Go back to a token that is already open so more items land on the same
  // customer's bill. A token stays open until it is paid, so a customer can
  // keep ordering on the same number right up to closing the counter.
  const selectToken = (tokenNumber) => {
    const number = Number(tokenNumber);
    if (!number) {
      return;
    }
    dispatch({ type: 'SET_CURRENT_TOKEN', payload: number });
  };

  // Drop the token once it has been paid for
  const clearCurrentToken = () => {
    dispatch({ type: 'SET_CURRENT_TOKEN', payload: null });
  };

  // tokenNumber is passed in by the caller that has just issued one: a token
  // taken in the same click has not reached state yet, and reading it from
  // there would put the item on the previous customer's bill.
  const addOrder = async (itemId, quantity = 1, tokenNumber = currentToken) => {
    if (!tokenNumber) {
      throw new Error('Create a token number before adding items');
    }

    try {
      const response = await api.post(`/api/items/${itemId}/order`, {
        quantity,
        tokenNumber
      });

      dispatch({ type: 'ADD_ORDER', payload: response.data });
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.response?.data?.message || error.message });
      throw error;
    }
  };

  // Take units off a line, removing it entirely when it reaches zero
  const decreaseOrder = async (orderId, quantity = 1) => {
    try {
      const response = await api.put(`/api/orders/${orderId}/decrease`, { quantity });
      dispatch({ type: 'DECREASE_ORDER', payload: response.data });
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  // Fetch today's revenue from daily reports API
  const fetchRevenue = useCallback(async () => {
    try {
      const response = await api.get('/api/reports/daily');
      return response.data.totalRevenue || 0;
    } catch (error) {
      console.error('Error fetching revenue:', error);
      return 0;
    }
  }, []);

  // dueDetails carries the customer name and mobile a credit sale is booked
  // against - ignored by the server for cash and bank
  const completeOrder = async (orderId, paymentType, discountData = {}, dueDetails = null) => {
    try {
      const response = await api.put(`/api/orders/${orderId}/complete`, {
        paymentType,
        discountAmount: discountData.discountAmount || 0,
        discountPercentage: discountData.discountPercentage || 0,
        ...(dueDetails || {})
      });
      dispatch({ type: 'COMPLETE_ORDER', payload: response.data.order });
      // Refresh items to get updated stock from server
      await fetchItems();
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const bulkCompleteOrders = async (orderIds, paymentType, discountData = {}, dueDetails = null) => {
    try {
      const response = await api.put('/api/orders/bulk/complete', {
        orderIds,
        paymentType,
        discountAmount: discountData.discountAmount || 0,
        discountPercentage: discountData.discountPercentage || 0,
        ...(dueDetails || {})
      });
      
      // Remove completed orders from active orders
      orderIds.forEach(orderId => {
        const order = state.activeOrders.find(o => o._id === orderId);
        if (order) {
          dispatch({ type: 'COMPLETE_ORDER', payload: order });
        }
      });
      
      // Refresh items to get updated stock from server
      await fetchItems();
      
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const removeOrder = async (orderId) => {
    try {
      // Find the order to get its details before deletion
      const orderToRemove = state.activeOrders.find(order => order._id === orderId);
      if (!orderToRemove) {
        throw new Error('Order not found');
      }

      const response = await api.delete(`/api/orders/${orderId}`);
      dispatch({ type: 'REMOVE_ORDER', payload: orderToRemove });
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  /**
   * Clears the day's unpaid lines and puts their stock back. Completed sales
   * and expenses are left alone - the reports are built from those.
   */
  const clearUnpaidLines = async () => {
    const response = await api.post('/api/reports/reset-day', {});
    await Promise.all([fetchActiveOrders(), fetchItems()]);
    dispatch({ type: 'SET_CURRENT_TOKEN', payload: null });
    return response.data;
  };

  const addExpense = async (expenseData) => {
    try {
      const response = await api.post('/api/expenses', expenseData);
      dispatch({ type: 'ADD_EXPENSE', payload: response.data });
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const addItem = async (itemData) => {
    try {
      const response = await api.post('/api/items', itemData);
      dispatch({ type: 'ADD_ITEM', payload: response.data });
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const updateItem = async (itemId, itemData) => {
    try {
      const response = await api.put(`/api/items/${itemId}`, itemData);
      dispatch({ type: 'UPDATE_ITEM', payload: response.data });
      return response.data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await api.delete(`/api/items/${itemId}`);
      dispatch({ type: 'DELETE_ITEM', payload: itemId });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const initializeItems = async () => {
    try {
      await api.post('/api/items/initialize');
      fetchItems();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // Load data as soon as the user is authenticated, and keep it fresh.
  //
  // This used to read localStorage once on mount. CafeProvider mounts above the
  // router, before anyone has logged in, so that read always came back empty:
  // it returned early, never fetched, and - because the early return skipped the
  // setInterval too - never started the refresh poll either. The menu then only
  // appeared if a separate timer on the Dashboard happened to win the race,
  // which is why it sometimes took a reload to show up.
  useEffect(() => {
    // A sales agent belongs to no tenant, so every one of these calls comes
    // back 403. Without this they would fire on load and again every 30
    // seconds, filling the console with errors for data they cannot have.
    if (!isAuthenticated || userRole === 'sales') {
      // Drop the previous session's data so it can't show for the next user
      dispatch({ type: 'SET_ITEMS', payload: [] });
      dispatch({ type: 'SET_ACTIVE_ORDERS', payload: [] });
      return undefined;
    }

    fetchItems();
    fetchActiveOrders();
    fetchCounterSession();

    // Auto-refresh every 30 seconds. The counter session is polled with the
    // rest: it can be opened or closed from another till or the mobile app, and
    // until this browser sees that it would keep offering to add items to a
    // session that no longer exists.
    const interval = setInterval(() => {
      fetchActiveOrders();
      fetchItems();
      fetchCounterSession();
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, userRole, fetchItems, fetchActiveOrders, fetchCounterSession]);

  const refreshItems = async () => {
    await fetchItems();
  };

  const refreshActiveOrders = async () => {
    await fetchActiveOrders();
  };

  const value = {
    ...state,
    // Overrides the raw stored value in state: only a token the open session
    // actually issued counts as the one being served.
    currentToken,
    tokenIsStale,
    tokensIssued,
    fetchItems,
    fetchActiveOrders,
    fetchRevenue,
    refreshItems,
    refreshActiveOrders,
    fetchCounterSession,
    fetchCounterSummary,
    fetchCounterHistory,
    openCounter,
    closeCounter,
    issueToken,
    ensureToken,
    selectToken,
    clearCurrentToken,
    clearUnpaidLines,
    addOrder,
    decreaseOrder,
    completeOrder,
    bulkCompleteOrders,
    removeOrder,
    addExpense,
    addItem,
    updateItem,
    deleteItem,
    initializeItems
  };

  return (
    <CafeContext.Provider value={value}>
      {children}
    </CafeContext.Provider>
  );
}

export function useCafe() {
  const context = useContext(CafeContext);
  if (!context) {
    throw new Error('useCafe must be used within a CafeProvider');
  }
  return context;
}